// API Configuration for Branch Router
// CRA inlines NODE_ENV at build time: "development" for npm start, "production" for npm run build.
const isDevelopmentBuild = process.env.NODE_ENV === 'development';

function loopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/** True only for a full absolute http(s) URL with a real host (rejects `http://`, typos, relative strings). */
function isUsableAbsoluteApiBase(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  try {
    const u = new URL(t);
    if (!u.hostname) return false;
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * When the page is HTTPS, never keep an http:// API base (mixed content). Same host → use
 * relative /api/... (nginx). Different host → upgrade scheme to https:.
 */
export function alignApiBaseWithPageOrigin(explicit: string): string {
  const trimmed = explicit.trim();
  if (!trimmed || typeof window === 'undefined') {
    return trimmed;
  }
  if (!isUsableAbsoluteApiBase(trimmed)) {
    return '';
  }

  try {
    const page = window.location;
    const api = new URL(trimmed);

    if (page.protocol === 'https:' && api.protocol === 'http:') {
      if (api.hostname === page.hostname) {
        return '';
      }
      api.protocol = 'https:';
      return api.origin.replace(/\/$/, '');
    }

    if (page.origin !== api.origin && loopbackHost(page.hostname) && loopbackHost(api.hostname)) {
      return '';
    }
  } catch {
    return '';
  }

  return trimmed.replace(/\/$/, '');
}

/**
 * Resolve API base URL from REACT_APP_API_URL (build-time) with runtime HTTPS alignment.
 */
export function resolveApiBaseUrl(envValue: string | undefined): string {
  let explicit = (envValue || '').trim();
  if (explicit && !isUsableAbsoluteApiBase(explicit)) {
    explicit = '';
  }

  const hasWindow = typeof window !== 'undefined';

  if (explicit && hasWindow) {
    explicit = alignApiBaseWithPageOrigin(explicit);
  }

  const isLocalBrowser = hasWindow && loopbackHost(window.location.hostname);
  if (!explicit && isDevelopmentBuild && isLocalBrowser) {
    return 'http://localhost:8000';
  }

  return explicit;
}

/** Runtime API base (re-evaluates page protocol so HTTPS staging fixes http:// build args). */
export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    const env = (process.env.REACT_APP_API_URL || '').trim();
    if (!env || !isUsableAbsoluteApiBase(env)) {
      return '';
    }
    if (/^http:\/\//i.test(env)) {
      return alignApiBaseWithPageOrigin(env);
    }
    if (/^https:\/\//i.test(env)) {
      return env.replace(/\/$/, '');
    }
    return '';
  }
  return resolveApiBaseUrl(process.env.REACT_APP_API_URL);
}

/** FastAPI collection routes use a trailing slash; omitting it yields 307 Location: http:// behind nginx. */
const COLLECTION_PATHS_NEEDING_TRAILING_SLASH = ['/api/v1/alerts', '/api/v1/webhooks'] as const;

export function ensureTrailingSlashBeforeQuery(url: string): string {
  let out = url;
  for (const collection of COLLECTION_PATHS_NEEDING_TRAILING_SLASH) {
    const escaped = collection.replace(/\//g, '\\/');
    const re = new RegExp(`(${escaped})(?!\\/)(\\?|#|$)`);
    out = out.replace(re, `$1/$2`);
  }
  return out;
}

/**
 * Final guard: never fetch http:// from an https:// page (mixed content), even if a stale
 * bundle baked REACT_APP_API_URL=http://... into a code path that skipped getApiBaseUrl.
 */
export function ensureSecureRequestUrl(url: string): string {
  if (!url) {
    return url;
  }
  let normalized = ensureTrailingSlashBeforeQuery(url);
  if (typeof window === 'undefined') {
    return normalized;
  }
  const page = window.location;
  if (page.protocol !== 'https:') {
    return normalized;
  }
  try {
    if (normalized.startsWith('/')) {
      return normalized;
    }
    const resolved = new URL(normalized, page.origin);
    resolved.pathname = ensureTrailingSlashBeforeQuery(resolved.pathname);
    if (resolved.protocol === 'https:') {
      return resolved.href;
    }
    if (resolved.hostname === page.hostname) {
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    }
    resolved.protocol = 'https:';
    return resolved.href;
  } catch {
    return normalized;
  }
}

export const API_CONFIG = {
  /** Prefer getApiBaseUrl() / buildApiUrl(); getter avoids stale http:// on HTTPS pages. */
  get BASE_URL(): string {
    return getApiBaseUrl();
  },

  // API endpoints
  ENDPOINTS: {
    // Health and status
    HEALTH: '/api/v1/system/health-check',
    STATUS: '/api/v1/system/status',
    EMERGENCY_STATUS: '/api/v1/system/emergency-status/public',

    // Authentication
    LOGIN: '/api/v1/auth/login',
    LOGOUT: '/api/v1/auth/logout',
    REFRESH: '/api/v1/auth/refresh',
    ME: '/api/v1/auth/profile',
    REGISTER: '/api/v1/auth/register',
    PASSWORD_RESET: '/api/v1/auth/password-reset',
    PASSWORD_RESET_CONFIRM: '/api/v1/auth/password-reset/confirm',
    CHANGE_PASSWORD: '/api/v1/auth/change-password',
    REAUTHENTICATE: '/api/v1/auth/reauthenticate',
    VERIFY_EMAIL: '/api/v1/auth/verify-email',
    RESEND_VERIFICATION: '/api/v1/auth/resend-verification',

    // Trading alerts
    ALERTS: '/api/v1/alerts/public',
    /** Authenticated alert execution log (AlertLog rows); query: skip, limit */
    /** Trailing slash required — FastAPI route is GET /alerts/; without slash, 307 may redirect to http:// behind proxy */
    ALERT_LOGS: '/api/v1/alerts/',

    // Webhook management
    WEBHOOKS: '/api/v1/webhooks/',
    WEBHOOK_MAPPINGS: '/api/v1/webhooks/',
    WEBHOOK_TEST: '/api/v1/webhooks/test',

    // System monitoring
    METRICS: '/api/v1/monitoring/metrics/public',
    PERFORMANCE: '/api/v1/performance/metrics/public',
    SYSTEM_STATUS: '/api/v1/system/status',

    // Safety controls
    EMERGENCY_STOP: '/api/v1/system/emergency-stop',
    SAFETY_STATUS: '/api/v1/system/emergency-status/public',

    // Configuration
    CONFIG: '/api/v1/config',
    EMAIL_CONFIG: '/api/v1/users/email-configs',

    // Dashboard
    DASHBOARD_OVERVIEW: '/api/v1/dashboard/overview',
    DASHBOARD_CONFIG: '/api/v1/dashboard/config',
    DASHBOARD_WIDGETS: '/api/v1/dashboard/widgets',

    // Admin
    ADMIN_DASHBOARD: '/api/v1/admin/dashboard',
    ADMIN_USERS: '/api/v1/users/admin/users',
    ADMIN_SYSTEM_CONFIG: '/api/v1/admin/system-configurations',
    ADMIN_AUDIT_LOGS: '/api/v1/admin/audit-logs',
    ADMIN_SECURITY_EVENTS: '/api/v1/admin/security-events',

    // Email Management
    EMAIL_ADDRESSES: '/api/v1/email-addresses',
    EMAIL_DOMAINS: '/api/v1/email-domains',
    EMAIL_SERVERS: '/api/v1/email-servers',

    // Alert Configuration
    ALERT_CONFIGURATIONS: '/api/v1/alert-configurations',

    // Incoming webhook trigger (token-based auth for external callers)
    INCOMING_WEBHOOK_TOKEN: '/api/v1/incoming-webhook/token',
    INCOMING_WEBHOOK_GENERATE_TOKEN: '/api/v1/incoming-webhook/generate-token',
    INCOMING_WEBHOOK_ACTIVITY: '/api/v1/incoming-webhook/activity',
    INCOMING_WEBHOOK_TRIGGER_PATH: '/api/v1/incoming-webhook/trigger',
    INCOMING_WEBHOOK_ALERT_TOKEN: (alertId: number) => `/api/v1/incoming-webhook/alert/${alertId}/token`,
    INCOMING_WEBHOOK_ALERT_GENERATE_TOKEN: (alertId: number) =>
      `/api/v1/incoming-webhook/alert/${alertId}/generate-token`,
    INCOMING_WEBHOOK_ALERT_SLOT_TOKEN: (alertId: number, slotIndex: number) =>
      `/api/v1/incoming-webhook/alert/${alertId}/slot/${slotIndex}/token`,
    INCOMING_WEBHOOK_ALERT_SLOT_GENERATE_TOKEN: (alertId: number, slotIndex: number) =>
      `/api/v1/incoming-webhook/alert/${alertId}/slot/${slotIndex}/generate-token`,
  },

  // Request timeout (in milliseconds)
  TIMEOUT: 30000,

  // Retry configuration
  RETRY: {
    ATTEMPTS: 3,
    DELAY: 1000,
  },

  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

// Helper function to build full URL
export const buildApiUrl = (endpoint: string): string => {
  const path = ensureTrailingSlashBeforeQuery(
    endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  );
  const base = getApiBaseUrl();
  const combined = base ? `${base.replace(/\/$/, '')}${path}` : path;
  return ensureSecureRequestUrl(combined);
};

// Helper function to get auth headers
export const getAuthHeaders = (token?: string): Record<string, string> => {
  const headers: Record<string, string> = { ...API_CONFIG.DEFAULT_HEADERS };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};
